import React, { useEffect, useState } from 'react'
import { Col, Nav, Row, Tab } from 'react-bootstrap'
import _ from 'lodash'

import Overview from './overview'
import EmailVerifier from './email_verifier'
import EmailFinder from './email_finder'
import Prospect from './prospect'
import FormGuard from './form_guard'
import DataPulse from './data_pulse'
import Team from './team'

import { ApiHelper } from '../../../helpers/apihelper'
import * as Constants from '../../../helpers/constants'
import {
  ANALYTICS_TAB_LABELS,
  buildMemberOptions,
  buildSelfMemberOption,
  getVisibleTabs,
  isOrgAdminView
} from './helpers'

import '../../../static/css/usage_reports.css'
import '../../../static/css/circle.css'
import './analytics.css'

const TAB_COMPONENTS = {
  overview: Overview,
  email_verifier: EmailVerifier,
  email_finder: EmailFinder,
  prospect: Prospect,
  form_guard: FormGuard,
  data_pulse: DataPulse,
  team: Team,
}

/**
 * Shared Analytics module — exact replica of the cowebapp Analytics pages
 * (app.clearout.io/analytics/*) rendered for the customer the CRM admin is
 * inspecting. `viewedUser` = {id, email, name, user_type, user_role, org_id}
 * drives role-based visibility so the admin sees exactly what that customer
 * sees in their own app.
 */
const AnalyticsModule = ({ client, viewedUser }) => {
  const visibleTabs = getVisibleTabs(client, viewedUser)

  const [activeTab, setActiveTab] = useState(visibleTabs[0])
  const [memberOptions, setMemberOptions] = useState(() => (
    isOrgAdminView(viewedUser) ? [] : [buildSelfMemberOption(viewedUser)]
  ))

  // webapp getAllUsersList: member dropdown options for org owner/admin views
  useEffect(() => {
    if (!isOrgAdminView(viewedUser) || !viewedUser.org_id) return

    const requestBody = {
      skip: 0,
      limit: 100,
      search: '',
      sort: {},
      filters: { org_id: viewedUser.org_id }
    }

    const body = _.pick(requestBody, ApiHelper(client, 'MEMBERS_LIST').API_BODY)

    ApiHelper(client, 'MEMBERS_LIST').API(client, body)
      .then((res) => {
        if (res && res.status === Constants.CO_API.SUCCESS) {
          setMemberOptions(buildMemberOptions(res.data.result || []))
        }
      })
      .catch((err) => {
        console.log('members list err', err)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, viewedUser.org_id, viewedUser.user_type, viewedUser.user_role])

  if (_.isEmpty(visibleTabs)) {
    return (
      <div className="text-center p-3">
        <p className="m-0">No analytics available</p>
      </div>
    )
  }

  return (
    <Tab.Container
      defaultActiveKey={visibleTabs[0]}
      activeKey={activeTab}
      onSelect={(key) => setActiveTab(key)}
    >
      <Row>
        <Col sm={2}>
          <Nav
            variant="pills"
            className="flex-column"
          >
            {visibleTabs.map((tab) => (
              <Nav.Item key={tab}>
                <Nav.Link eventKey={tab}>{ANALYTICS_TAB_LABELS[tab]}</Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </Col>
        <Col sm={10}>
          <Tab.Content>
            {visibleTabs.map((tab) => {
              const TabComponent = TAB_COMPONENTS[tab]
              return (
                <Tab.Pane
                  key={tab}
                  eventKey={tab}
                >
                  {activeTab === tab && (
                    <TabComponent
                      client={client}
                      viewedUser={viewedUser}
                      memberOptions={memberOptions}
                    />
                  )}
                </Tab.Pane>
              )
            })}
          </Tab.Content>
        </Col>
      </Row>
    </Tab.Container>
  )
}

export default AnalyticsModule
